import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Loader2 } from 'lucide-react';
import { PageContainer } from '@/components/page-container';
import { toast } from 'sonner';
import { emit } from '@/lib/analytics';
import { useFetch } from '@/hooks/use-fetch';
import { useWorkspace } from '@/contexts/workspace-context';
import { parseApiError } from '@/lib/api-error';
import { captureException } from '@/lib/sentry';
import { readPopupContext, clearPopupContext } from '@/lib/meta-popup-context';
import { workspaceUrl } from '@/lib/workspace-url';
import { isInviteConnectPending, clearInviteConnectPending } from '@/lib/invite-connect';
import { PageShell } from './invite/page-shell';
import { useCompleteInvite } from './invite/use-complete-invite';
import type { WorkspacePublicId } from '@hookmyapp/shared';
import { WabaPhonePicker, type PickerPhone } from './meta-connecting/waba-phone-picker';

/**
 * Phase 118 — /w/:ws/channels/connecting page.
 *
 * Inside WorkspaceProvider (via /w/:ws subtree). Exchanges the Meta auth code
 * for a channel and routes the user to the freshly-created channel detail.
 *
 * Note: useFetch auto-injects `X-Workspace-Id` from `useWorkspace().activeWorkspace.id`.
 * WorkspaceLayout calls `switchWorkspace(:ws)` on mount, so by the time this
 * page fetches the header value equals the URL ws param.
 */

const GENERIC_CONNECT_ERROR =
  "We couldn't connect you to Meta right now. Please try again in a moment.";

// 409 codes whose backend message is surfaced verbatim (per CONTEXT.md §Meta
// connect flow the default is the generic copy; these cross-workspace
// conflicts are the exception so the user knows the WABA/phone is still
// bound to another workspace).
// Codes whose backend message must be surfaced verbatim — the generic
// "try again" copy is wrong advice for them: the cross-workspace 409s need
// the disconnect-first explanation, and a burned/mismatched pending
// capability (Stage 2 picker) requires redoing the Meta connect, not a retry.
const VERBATIM_ERROR_CODES = new Set([
  'NUMBER_CONNECTED_ELSEWHERE', // 409 — cross-org / degraded; "disconnect elsewhere first"
  'INSTAGRAM_ACCOUNT_ALREADY_CONNECTED', // 409 — IG account connected to another workspace's channel
  'REONBOARDING_NOT_ACTIVE', // 409 — bake-window gate; "not yet reconnectable"
  'CHANNEL_PHONE_OWNED_BY_OTHER_WORKSPACE', // 409 — Stage 2 picker cross-workspace preflight
  'META_PENDING_CONSUMED', // 409
  'META_PENDING_PHONE_NOT_IN_SET', // 400
  'META_PENDING_USER_MISMATCH', // 403
  'META_REGISTER_CREDENTIAL', // 401 — AIT-618: Meta grant lost access; reconnect + approve
  'META_REGISTER_PIN_MISMATCH', // 400 — AIT-618: two-step verification blocks activation
  'META_REGISTER_FAILED', // 400 — AIT-618: Meta refused to activate the number
]);

function verbatimErrorMessage(status: number, body: unknown): string | null {
  if (status < 400 || status >= 500 || typeof body !== 'object' || body === null) return null;
  const { code, message } = body as { code?: unknown; message?: unknown };
  if (typeof code !== 'string' || !VERBATIM_ERROR_CODES.has(code)) return null;
  return typeof message === 'string' ? message : null;
}

/**
 * Success body of POST /meta/signup (flat channel DTO, byte-unchanged by
 * Stage 2) and POST /meta/signup/select (same flat DTO). `publicId` /
 * snake_case spellings are defensive tolerances — see the PostHog capture
 * comments below.
 */
interface SignupSuccessData {
  id: string;
  publicId?: string;
  metaResourceId?: string;
  metaWabaId?: string;
  waba_id?: string;
  phone_number_id?: string;
  whatsappPhoneNumberId?: string;
  phoneNumberId?: string;
}

export function MetaConnectingPage() {
  const { ws } = useParams<{ ws: WorkspacePublicId }>();
  const navigate = useNavigate();
  const fetchWithAuth = useFetch();
  const { refreshChannels } = useWorkspace();
  // StrictMode double-invocation guard — don't POST /meta/signup twice.
  const processedRef = useRef(false);
  // Stage 2 multi-WABA picker — set when POST /meta/signup returns
  // `selection_required`; the page stays mounted and renders the picker.
  // picker state carries the optional reconnect context the backend returned
  // (the caller's OWN target phone id — D4 create-new fork).
  const [picker, setPicker] = useState<
    { pendingId: string; phones: PickerPhone[]; reconnect?: { targetPhoneNumberId: string } } | null
  >(null);
  const [submitting, setSubmitting] = useState(false);
  // AIT-529 — snapshot once: onConnected clears the flag before navigating, and
  // a live read would flip the page back to app chrome mid-exit.
  const [inviteMode] = useState(() => isInviteConnectPending());
  // Second, later attempt at the affiliate grant (Codex review P1). A no-op
  // for a normal in-app connect: without a stored invite context the hook
  // never fires.
  useCompleteInvite();

  const displayText = 'Connecting your channel…';

  // AIT-529 / Codex review (P2): every failure exit must stay inside whichever
  // flow the user is in. An invited client dropped onto /w/:ws/channels lands
  // in a workspace UI they were never meant to see, with the invite-mode flag
  // still set for a later connect to trip over.
  const failureDestination = () => (inviteMode ? '/invite/connect' : workspaceUrl(ws!, 'channels'));

  // Helper — fire toast then defer navigate to a macrotask. Without the
  // defer, React 19 batches the toast state update + the navigate together,
  // and MetaConnectingPage unmount can race-cancel the toast render.
  const toastThenNav = (msg: string, to: string) => {
    toast.error(msg);
    setTimeout(() => navigate(to, { replace: true }), 0);
  };

  // Shared post-success handling for both the direct /meta/signup path and
  // the picker's /meta/signup/select path. `ct` is a parameter because the
  // initial path can be 'instagram_login' while the picker path is always
  // 'whatsapp' (the picker only exists for the WhatsApp multi-WABA flow).
  const onConnected = async (data: SignupSuccessData, ct: string) => {
    if (!ws) return;
    // Phase 113 — funnel stage 8 marker (113-CONTEXT.md §Canonical
    // funnel stages). Fires on successful POST /meta/signup, capturing
    // the newly-created channel's identifiers while the response body
    // is still in scope and BEFORE refreshChannels()/navigate() swap
    // the UI. `data.publicId ?? data.id` is defensive — the backend
    // /meta/signup response returns the ch_ publicId as `id` post
    // Phase 117, but we tolerate either spelling.
    if (ct === 'instagram_login') {
      emit('instagram_connected', {
        channel_public_id: data.publicId ?? data.id,
        ig_user_id: data.metaResourceId,
      });
    } else {
      emit('waba_connected', {
        channel_public_id: data.publicId ?? data.id,
        waba_id: data.metaWabaId ?? data.waba_id,
        phone_number_id:
          data.phone_number_id ?? data.whatsappPhoneNumberId ?? data.phoneNumberId,
      });
    }
    // The first render after an OAuth deep-link can still hold the previously
    // active workspace. Invalidate the URL target explicitly so the new
    // channel appears in that customer's sidebar without a hard refresh.
    await refreshChannels(ws);
    // Preserves the pre-118 success UX — the OAuth exchange used to
    // live in dashboard-layout and emitted this toast on success.
    // Defer via macrotask so Sonner can commit the render before the
    // MetaConnectingPage unmount (same React 19 race as the error path).
    const successMsg =
      ct === 'instagram_login'
        ? 'Instagram channel connected'
        : 'WhatsApp channel connected';
    // AIT-529 — an invited client never enters the workspace: their flow ends
    // on the standalone done screen, which IS the success feedback (no toast
    // for a UI they are leaving behind).
    if (isInviteConnectPending()) {
      clearInviteConnectPending();
      navigate('/invite/done', { replace: true });
      return;
    }
    setTimeout(() => {
      toast.success(successMsg);
    }, 0);
    navigate(workspaceUrl(ws, `channels/${data.id}`), { replace: true });
  };

  // Shared non-2xx handling for both paths. Per CONTEXT.md §Meta connect
  // flow: default to the spec copy on error. Exception — surface the backend
  // message verbatim for VERBATIM_ERROR_CODES (cross-workspace conflicts +
  // burned/mismatched pending capability), where the generic copy misleads.
  const onConnectFailed = (status: number, body: unknown) => {
    if (!ws) return;
    parseApiError(status, body, GENERIC_CONNECT_ERROR);
    // AppError-derived SEV3 responses (4xx with a structured `code` —
    // CLAUDE.md error policy) are expected user-side conditions; the
    // toast handles UX. Only capture 5xx + unstructured 4xx, which
    // signal a real backend/contract bug.
    const hasStructuredCode =
      typeof body === 'object' &&
      body !== null &&
      typeof (body as { code?: unknown }).code === 'string';
    if (status >= 500 || !hasStructuredCode) {
      captureException(new Error('meta connect failed'), {
        tag: 'meta_connect_error',
        status,
        body,
      });
    }
    // AIT-529 — invited clients go back to their standalone picker, not into
    // the workspace channel list. The pending flag stays set so a retry still
    // lands standalone.
    toastThenNav(verbatimErrorMessage(status, body) ?? GENERIC_CONNECT_ERROR, failureDestination());
  };

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    if (!ws) {
      navigate('/', { replace: true });
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const ct = params.get('channelType') ?? 'whatsapp';
    const endpoint = ct === 'instagram_login' ? '/instagram/signup' : '/meta/signup';
    if (!code || !state) {
      navigate(failureDestination(), { replace: true });
      return;
    }

    (async () => {
      // Phase 124 (meta signup observability) — forward the popup-context
      // envelope stashed in sessionStorage by useMetaSignupListeners /
      // MetaCallbackPage. The envelope is keyed by the OAuth `state` value so
      // a stale envelope from a previous attempt cannot leak into this one.
      // We read once up-front and clear in a `finally` so every exit path
      // (success, non-2xx, thrown error) removes the key — preventing stale
      // envelopes from being forwarded on a retry.
      const popupContext = readPopupContext(state);
      const requestBody: Record<string, unknown> = { code, state };
      if (popupContext) requestBody.popupContext = popupContext;
      try {
        const res = await fetchWithAuth(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': ws },
          body: JSON.stringify(requestBody),
        });
        if (res.ok) {
          const data = await res.json();
          // Stage 2 — multi-WABA tokens return `selection_required` instead
          // of the flat channel DTO. Stay mounted and render the picker; the
          // user's choice completes via POST /meta/signup/select below.
          if (data?.status === 'selection_required') {
            setPicker({ pendingId: data.pendingId, phones: data.phones, reconnect: data.reconnect });
            return;
          }
          await onConnected(data, ct);
        } else {
          const body = await res.json().catch(() => null);
          onConnectFailed(res.status, body);
        }
      } catch (e) {
        captureException(e, { tag: 'meta_connect_network' });
        toastThenNav(GENERIC_CONNECT_ERROR, failureDestination());
      } finally {
        clearPopupContext(state);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, navigate, fetchWithAuth, refreshChannels]);

  // `picker` can only be set after the effect verified `ws`, so the `ws`
  // half of this guard is type-narrowing for workspaceUrl, not a reachable
  // state.
  if (picker && ws) {
    const handleSelect = async (phoneNumberId: string) => {
      // Entry guard: the button's disabled state only lands after re-render,
      // so a fast double-click would fire two /select POSTs. The backend CAS
      // makes the second a 409, but one request is the correct shape.
      if (submitting) return;
      setSubmitting(true);
      try {
        const res = await fetchWithAuth('/meta/signup/select', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': ws },
          body: JSON.stringify({
            pendingId: picker.pendingId,
            phoneNumberId,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          onConnectFailed(res.status, body);
          return;
        }
        await onConnected(await res.json(), 'whatsapp'); // picker is WhatsApp-only
      } catch (e) {
        captureException(e, { tag: 'meta_connect_network' });
        toastThenNav(GENERIC_CONNECT_ERROR, failureDestination());
      }
      // No setSubmitting(false) reset — every branch above navigates away,
      // unmounting this component; a reset would be a no-op on an unmounted tree.
    };
    const phonePicker = (
      <WabaPhonePicker
        phones={picker.phones}
        submitting={submitting}
        onSelect={handleSelect}
        reconnect={picker.reconnect}
        backTo={{
          href: failureDestination(),
          label: inviteMode ? 'Connect another channel' : 'Back to channels',
        }}
      />
    );
    return inviteMode ? (
      <PageShell>
        <div className="relative z-10 w-full max-w-md">{phonePicker}</div>
      </PageShell>
    ) : (
      phonePicker
    );
  }

  // The invited client never sees app chrome: this page is one step of the
  // standalone invite flow, so it wears that flow's surface (dark card,
  // vertically centred) rather than the in-app page container.
  if (inviteMode) {
    return (
      <PageShell>
        <div className="relative z-10 flex flex-col items-center gap-4">
          <Loader2 className="size-8 animate-spin text-white/70" />
          <p className="text-lg font-medium text-white">{displayText}</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageContainer variant="fluid" className="flex flex-col items-center justify-center gap-4 py-24">
      <Loader2 className="size-8 animate-spin text-muted-foreground" />
      <p className="text-lg font-medium">{displayText}</p>
    </PageContainer>
  );
}
